import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { IonicRouteStrategy } from '@ionic/angular/standalone';

/**
 * Estrategia personalizada de reutilización de rutas que:
 * 1. Hereda el comportamiento de IonicRouteStrategy (para mantener compatibilidad con Ionic)
 * 2. Desactiva el caché SOLO para rutas específicas del expediente digital
 *
 * PROBLEMA RESUELTO:
 * - IonicRouteStrategy cachea componentes entre navegaciones
 * - Esto impide que se recarguen las notas médicas al cambiar el select
 *
 * SOLUCIÓN:
 * - Detecta rutas de notas médicas (consulta-medica, nota-urgencias, etc.)
 * - Retorna false en shouldReuseRoute() para forzar destrucción del componente
 * - Permite recarga completa al navegar entre notas
 */
export class CustomRouteReuseStrategy extends IonicRouteStrategy {
  /**
   * Rutas del expediente digital que NO deben cachearse
   * Estas rutas se recargarán completamente en cada navegación
   */
  private readonly NO_CACHE_ROUTES = [
    '/expediente/expediente-digital/consulta-medica',
    '/expediente/expediente-digital/nota-urgencias',
    '/expediente/expediente-digital/nota-ingreso',
    '/expediente/expediente-digital/nota-evolucion',
    '/expediente/expediente-digital/nota-egreso',
  ];

  /**
   * Verifica si la ruta actual es una nota médica que NO debe cachearse
   */
  private isNotaMedica(route: ActivatedRouteSnapshot): boolean {
    const fullPath = this.getFullPath(route);
    return this.NO_CACHE_ROUTES.some(noCache => fullPath.includes(noCache));
  }

  /**
   * Construye la ruta completa desde el snapshot
   */
  private getFullPath(route: ActivatedRouteSnapshot): string {
    const segments: string[] = [];
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      if (current.url.length > 0) {
        segments.unshift(...current.url.map(segment => segment.path));
      }
      current = current.parent;
    }

    return '/' + segments.join('/');
  }

  /**
   * ⚠️ MÉTODO CRÍTICO: Determina si se debe reutilizar el componente
   *
   * @override
   * Si retorna true: reutiliza el componente existente (caché)
   * Si retorna false: destruye y recrea el componente (recarga)
   */
  override shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    // Si la ruta actual O la futura es una nota médica, NO reutilizar
    if (this.isNotaMedica(future) || this.isNotaMedica(curr)) {
      console.log('[CustomRouteReuseStrategy] NO reutilizando ruta de nota médica');
      console.log('[CustomRouteReuseStrategy] Ruta actual:', this.getFullPath(curr));
      console.log('[CustomRouteReuseStrategy] Ruta futura:', this.getFullPath(future));

      // ✅ IMPORTANTE: Siempre retornar false para notas médicas
      // Esto fuerza la destrucción y recreación del componente
      return false;
    }

    // Para otras rutas, usar comportamiento normal de Ionic
    return super.shouldReuseRoute(future, curr);
  }

  /**
   * @override
   * Desactiva almacenamiento de componentes para notas médicas
   */
  override shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (this.isNotaMedica(route)) {
      console.log('[CustomRouteReuseStrategy] NO almacenando nota médica');
      return false;
    }
    return super.shouldDetach(route);
  }

  /**
   * @override
   * Desactiva adjuntar componentes cacheados para notas médicas
   */
  override shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (this.isNotaMedica(route)) {
      console.log('[CustomRouteReuseStrategy] NO adjuntando nota médica cacheada');
      return false;
    }
    return super.shouldAttach(route);
  }

  /**
   * @override
   * No almacena handles para notas médicas
   */
  override store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (this.isNotaMedica(route)) {
      console.log('[CustomRouteReuseStrategy] NO guardando handle de nota médica');
      return;
    }
    if (handle !== null) {
      super.store(route, handle);
    }
  }
}
